/**
 * API Service for fetching data from MySQL backend
 */

import { Contract, TemplateSection, ContractDocument, CompanySettings } from './types';

export interface HorseListResponse {
  ponyNextID: number;
  ponys: { [key: string]: any };
}

export interface ContactListResponse {
  contactNextID: number;
  contacts: { [key: string]: any };
}

// Get base path for API calls (app is at domain root)
const getBasePath = () => {
  return '/databaseOps/';
};

/**
 * Get authentication headers with JWT token
 */
const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('auth_token');
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
};

/**
 * Fetch horse list from MySQL
 */
export async function fetchHorseList(): Promise<HorseListResponse> {
  const response = await fetch(`${getBasePath()}getHorseList.php`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch horse list: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Fetch contact list from MySQL
 */
export async function fetchContactList(): Promise<ContactListResponse> {
  const response = await fetch(`${getBasePath()}getContactList.php`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch contact list: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Save horse list to MySQL
 */
export async function saveHorseList(data: HorseListResponse): Promise<void> {
  const response = await fetch(`${getBasePath()}saveHorseList.php`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(`Failed to save horse list: ${response.statusText}`);
  }
}

/**
 * Save contact list to MySQL
 */
export async function saveContactList(data: ContactListResponse): Promise<void> {
  const response = await fetch(`${getBasePath()}saveContactList.php`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(`Failed to save contact list: ${response.statusText}`);
  }
}

/**
 * Fetch contract history for a specific horse (all contracts, including hidden/inactive)
 */
export async function fetchContractHistory(horseId: string): Promise<Contract[]> {
  const response = await fetch(`${getBasePath()}getContracts.php?horse_id=${encodeURIComponent(horseId)}&include_hidden=1`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch contract history: ${response.statusText}`);
  }

  const result = await response.json();
  if (!result.success) {
    throw new Error(result.error || 'Failed to fetch contracts');
  }

  return result.contracts;
}

/**
 * Save contract to MySQL
 */
export async function saveContract(data: any): Promise<any> {
  const response = await fetch(`${getBasePath()}saveContract.php`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    throw new Error(`Failed to save contract: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Fetch contract template sections
 */
/**
 * Fetch company settings
 */
export async function fetchCompanySettings(): Promise<CompanySettings> {
  const response = await fetch(`${getBasePath()}getCompanySettings.php`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error(`Failed to fetch company settings: ${response.statusText}`);
  const result = await response.json();
  if (!result.success) throw new Error(result.error || 'Failed to fetch settings');
  return result.settings;
}

/**
 * Save company settings
 */
export async function saveCompanySettings(settings: Record<string, string>): Promise<void> {
  const response = await fetch(`${getBasePath()}saveCompanySettings.php`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ settings }),
  });
  if (!response.ok) throw new Error(`Failed to save settings: ${response.statusText}`);
  const result = await response.json();
  if (!result.success) throw new Error(result.error || 'Failed to save');
}

export async function fetchTemplateSections(includeInactive: boolean = false): Promise<TemplateSection[]> {
  const params = includeInactive ? '?include_inactive=1' : '';
  const response = await fetch(`${getBasePath()}getTemplateSections.php${params}`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch template sections: ${response.statusText}`);
  }

  const result = await response.json();
  if (!result.success) {
    throw new Error(result.error || 'Failed to fetch template sections');
  }

  return result.sections;
}

/**
 * Save a template section (create or update)
 */
export async function saveTemplateSection(section: Partial<TemplateSection>): Promise<{ section_id: number }> {
  const response = await fetch(`${getBasePath()}saveTemplateSection.php`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(section),
  });

  if (!response.ok) {
    throw new Error(`Failed to save template section: ${response.statusText}`);
  }

  const result = await response.json();
  if (!result.success) {
    throw new Error(result.error || 'Failed to save template section');
  }

  return result;
}

/**
 * Reorder template sections
 */
export async function reorderTemplateSections(order: { id: number; sort_order: number }[]): Promise<void> {
  const response = await fetch(`${getBasePath()}reorderTemplateSections.php`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ order }),
  });

  if (!response.ok) {
    throw new Error(`Failed to reorder template sections: ${response.statusText}`);
  }

  const result = await response.json();
  if (!result.success) {
    throw new Error(result.error || 'Failed to reorder');
  }
}

/**
 * Store a generated or uploaded contract PDF
 */
export async function storeContractPDF(
  pdfBlob: Blob,
  filename: string,
  contractId?: string,
  horseId?: string,
  documentType: string = 'generated'
): Promise<{ document_id: number }> {
  const formData = new FormData();
  formData.append('pdf', pdfBlob, filename);
  if (contractId) formData.append('contract_id', contractId);
  if (horseId) formData.append('horse_id', horseId);
  formData.append('document_type', documentType);

  const token = localStorage.getItem('auth_token');
  const response = await fetch(`${getBasePath()}storeContractPDF.php`, {
    method: 'POST',
    headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    body: formData,
  });

  if (!response.ok) {
    const errorBody = await response.text().catch(() => '');
    throw new Error(`Failed to store PDF (HTTP ${response.status}): ${errorBody || response.statusText}`);
  }

  const result = await response.json();
  if (!result.success) {
    throw new Error(result.error || 'Failed to store PDF');
  }

  return result;
}

/**
 * Fetch contract documents list
 */
export async function fetchContractDocuments(contractId?: string, horseId?: string): Promise<ContractDocument[]> {
  const params = new URLSearchParams();
  if (contractId) params.set('contract_id', contractId);
  if (horseId) params.set('horse_id', horseId);

  const response = await fetch(`${getBasePath()}getContractDocuments.php?${params}`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch documents: ${response.statusText}`);
  }

  const result = await response.json();
  if (!result.success) {
    throw new Error(result.error || 'Failed to fetch documents');
  }

  return result.documents;
}

/**
 * Get URL for viewing/downloading a contract document
 */
export function getContractDocumentUrl(documentId: number): string {
  const token = localStorage.getItem('auth_token');
  return `${getBasePath()}getContractDocument.php?id=${documentId}&token=${token}`;
}

/**
 * Delete a contract document
 */
export async function deleteContractDocument(documentId: number): Promise<void> {
  const response = await fetch(`${getBasePath()}deleteContractDocument.php`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ id: documentId }),
  });

  if (!response.ok) {
    throw new Error(`Failed to delete document: ${response.statusText}`);
  }

  const result = await response.json();
  if (!result.success) {
    throw new Error(result.error || 'Failed to delete document');
  }
}

/**
 * Analyze a contract document (OCR + LLM extraction + matching)
 */
export async function analyzeContractDocument(documentId: number): Promise<{
  extracted_data: any;
  matches: any[];
  ocr_length: number;
}> {
  const response = await fetch(`${getBasePath()}analyzeContractDocument.php`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ document_id: documentId }),
  });

  if (!response.ok) {
    throw new Error(`Analysis failed: ${response.statusText}`);
  }

  const result = await response.json();
  if (!result.success) {
    throw new Error(result.error || 'Analysis failed');
  }

  return result;
}

/**
 * Confirm or override document-to-contract match
 */
export async function matchContractDocument(
  documentId: number,
  horseId?: string,
  contractId?: string,
  action: 'confirm' | 'unmatch' = 'confirm'
): Promise<void> {
  const response = await fetch(`${getBasePath()}matchContractDocument.php`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ document_id: documentId, horse_id: horseId, contract_id: contractId, action }),
  });

  if (!response.ok) {
    throw new Error(`Match update failed: ${response.statusText}`);
  }

  const result = await response.json();
  if (!result.success) {
    throw new Error(result.error || 'Match update failed');
  }
}

/**
 * Change horse owner (ends old contract, creates new one)
 */
export async function changeOwner(
  horseId: string,
  changeDate: string,
  newOwner: { owner_name: string; owner_street?: string; owner_city?: string; owner_phone?: string; box_rent?: number }
): Promise<{ old_contract_id: string; new_contract_id: string }> {
  const response = await fetch(`${getBasePath()}changeOwner.php`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ horse_id: horseId, change_date: changeDate, new_owner: newOwner }),
  });

  const result = await response.json();
  if (!response.ok || !result.success) {
    throw new Error(result.error || 'Owner change failed');
  }
  return result;
}
